import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import WebtopLayoutContext from "../layout/WebtopLayoutContext";
import SaveToFile from "../files/SaveToFile";
import {
  getNotepadDocs,
  makeDoc,
  addDoc,
  updateDoc,
  removeDoc,
} from "../layout/layoutModel";

/**
 * NotepadApp — a Windows 11 Notepad-style text editor that opens as a Webtop
 * window (path /webtop-notepad): a tab strip across the top (one tab per
 * document, "+" to add, double-click a tab to rename), a full-width editor, and
 * a status bar (Ln/Col + word/character counts).
 *
 * Documents persist in the per-user layout (backend autosave, Phase 7).
 * Opened with ?fileId=<id>, it instead opens that file-system text file and
 * saves edits back to it (Windows: double-clicking a .txt opens Notepad).
 */
const countWords = (text) => {
  const t = (text || "").trim();
  return t ? t.split(/\s+/).length : 0;
};

/**
 * NotepadFile — Notepad editing a file-system text file (opened via ?fileId).
 * Loads the file's content, autosaves edits back to the file (debounced), and
 * supports Ctrl+S.
 */
const NotepadFile = ({ fileId }) => {
  const [doc, setDoc] = useState(null); // { id, name, content }
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("saved"); // saved | unsaved | saving
  const timerRef = useRef(null);
  const contentRef = useRef(""); // latest content, for flush-on-close

  // Flush a pending autosave when the file/window closes so edits aren't lost.
  useEffect(
    () => () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        import("../files/filesApi")
          .then(({ updateFileContent }) => updateFileContent(fileId, contentRef.current))
          .catch(() => {});
      }
    },
    [fileId]
  );

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const fapi = await import("../files/filesApi");
        const f = await fapi.getFile(fileId);
        if (!alive || !f) return;
        let content = f.content || "";
        // Legacy text files stored in S3 (type "file") keep their text in S3.
        if (!content && f.type === "file" && f.url) {
          try {
            content = await fapi.fetchText(f);
          } catch (e) {
            /* ignore */
          }
        }
        if (alive) setDoc({ id: f.id, name: f.name, content, type: f.type });
      } catch (e) {
        /* ignore */
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [fileId]);

  const persist = async (content) => {
    setStatus("saving");
    try {
      const { updateFileContent } = await import("../files/filesApi");
      await updateFileContent(fileId, content);
      setStatus("saved");
    } catch (e) {
      setStatus("unsaved");
    }
  };

  const onChange = (e) => {
    const content = e.target.value;
    contentRef.current = content;
    setDoc((d) => ({ ...d, content }));
    setStatus("unsaved");
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => persist(content), 1000);
  };

  const saveNow = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (doc) persist(doc.content);
  };
  const onKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && (e.key === "s" || e.key === "S")) {
      e.preventDefault();
      saveNow();
    }
  };

  if (loading) {
    return (
      <div className="webtop-notepad">
        <div className="webtop-notepad__empty">Loading…</div>
      </div>
    );
  }
  if (!doc) {
    return (
      <div className="webtop-notepad">
        <div className="webtop-notepad__empty">This file could not be opened.</div>
      </div>
    );
  }

  return (
    <div className="webtop-notepad">
      <div className="webtop-notepad__tabs">
        <div className="webtop-notepad__tab webtop-notepad__tab--active">
          <span className="webtop-notepad__tablabel">{doc.name}</span>
        </div>
        <div className="webtop-notepad__tabactions">
          <button type="button" className="webtop-notepad__save" onClick={saveNow}>
            Save
          </button>
        </div>
      </div>
      <textarea
        className="webtop-notepad__editor"
        value={doc.content}
        spellCheck={false}
        aria-label="File content"
        onChange={onChange}
        onKeyDown={onKeyDown}
      />
      <div className="webtop-notepad__status">
        <span className="webtop-notepad__status-cell webtop-notepad__status-save">
          {status === "saving" ? "Saving…" : status === "unsaved" ? "Unsaved" : "Saved"}
        </span>
        <span className="webtop-notepad__status-spacer" />
        <span className="webtop-notepad__status-cell">{countWords(doc.content)} words</span>
        <span className="webtop-notepad__status-cell">{(doc.content || "").length} characters</span>
      </div>
    </div>
  );
};

const NotepadApp = () => {
  const [params] = useSearchParams();
  const fileId = params.get("fileId");

  const { layout, updateLayout, saving, saveNow } = useContext(WebtopLayoutContext);
  const docs = getNotepadDocs(layout);

  const [activeId, setActiveId] = useState(null);
  const [renamingId, setRenamingId] = useState(null);
  const [caret, setCaret] = useState(0);

  const active = useMemo(() => {
    if (activeId) {
      const found = docs.find((d) => d.id === activeId);
      if (found) return found;
    }
    return docs[0] || null;
  }, [docs, activeId]);

  const createDoc = () => {
    const id = `doc-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    updateLayout({ notepadDocs: addDoc(layout, makeDoc(id, "Untitled")).notepadDocs });
    setActiveId(id);
    setCaret(0);
  };

  const patchDoc = (id, patch) =>
    updateLayout({ notepadDocs: updateDoc(layout, id, patch).notepadDocs });

  const deleteDoc = (id) => {
    updateLayout({ notepadDocs: removeDoc(layout, id).notepadDocs });
    if (activeId === id) setActiveId(null);
  };

  const selectDoc = (id) => {
    setActiveId(id);
    setCaret(0);
  };

  // Save to the dashboard (your account) now — flushes the debounced autosave.
  // Documents are stored in your per-user layout, NOT on the local disk.
  const saveToDashboard = () => {
    if (saveNow) saveNow();
  };

  // Ctrl/Cmd+S saves to the dashboard (mirrors Notepad's Save shortcut).
  const onEditorKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && (e.key === "s" || e.key === "S")) {
      e.preventDefault();
      saveToDashboard();
    }
  };

  // Status bar Ln/Col from the caret position in the active document.
  const content = active ? active.content || "" : "";
  const pos = Math.min(caret, content.length);
  const before = content.slice(0, pos);
  const ln = before.split("\n").length;
  const col = pos - before.lastIndexOf("\n");

  const onCaret = (e) => setCaret(e.target.selectionStart || 0);

  // Opened from a file (?fileId) → edit that file-system text file.
  if (fileId) return <NotepadFile fileId={fileId} />;

  return (
    <div className="webtop-notepad">
      <div className="webtop-notepad__tabs" role="tablist">
        {docs.map((d) => {
          const isActive = active && d.id === active.id;
          return (
            <div
              key={d.id}
              role="tab"
              aria-selected={isActive}
              className={`webtop-notepad__tab${isActive ? " webtop-notepad__tab--active" : ""}`}
              onClick={() => selectDoc(d.id)}
              onDoubleClick={() => setRenamingId(d.id)}
              title={`${d.title || "Untitled"} — double-click to rename`}
            >
              {renamingId === d.id ? (
                <input
                  className="webtop-notepad__tabinput"
                  autoFocus
                  value={d.title}
                  aria-label="Rename document"
                  onChange={(e) => patchDoc(d.id, { title: e.target.value })}
                  onBlur={() => setRenamingId(null)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === "Escape") setRenamingId(null);
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <span className="webtop-notepad__tablabel">{d.title || "Untitled"}</span>
              )}
              <button
                type="button"
                className="webtop-notepad__tabclose"
                aria-label={`Close ${d.title || "Untitled"}`}
                title="Close document"
                onClick={(e) => {
                  e.stopPropagation();
                  deleteDoc(d.id);
                }}
              >
                ✕
              </button>
            </div>
          );
        })}
        <button
          type="button"
          className="webtop-notepad__addtab"
          aria-label="New document"
          title="New document"
          onClick={createDoc}
        >
          +
        </button>

        <div className="webtop-notepad__tabactions">
          {active && <SaveToFile name={active.title} content={active.content} />}
          <button
            type="button"
            className="webtop-notepad__save"
            aria-label="Save to dashboard"
            title="Save to your dashboard (Ctrl+S)"
            onClick={saveToDashboard}
            disabled={!active}
          >
            Save
          </button>
        </div>
      </div>

      {active ? (
        <>
          <textarea
            className="webtop-notepad__editor"
            value={content}
            placeholder=""
            aria-label="Document content"
            spellCheck={false}
            onChange={(e) => patchDoc(active.id, { content: e.target.value })}
            onKeyDown={onEditorKeyDown}
            onKeyUp={onCaret}
            onClick={onCaret}
            onSelect={onCaret}
          />
          <div className="webtop-notepad__status">
            <span className="webtop-notepad__status-cell">
              Ln {ln}, Col {col}
            </span>
            <span
              className="webtop-notepad__status-cell webtop-notepad__status-save"
              title="Your notes are saved to your account automatically"
            >
              {saving ? "Saving…" : "Saved"}
            </span>
            <span className="webtop-notepad__status-spacer" />
            <span className="webtop-notepad__status-cell">{countWords(content)} words</span>
            <span className="webtop-notepad__status-cell">{content.length} characters</span>
            <span className="webtop-notepad__status-cell">UTF-8</span>
          </div>
        </>
      ) : (
        <div className="webtop-notepad__empty">
          <p>No document open.</p>
          <button type="button" className="webtop-notepad__emptybtn" onClick={createDoc}>
            + New document
          </button>
        </div>
      )}
    </div>
  );
};

export default NotepadApp;
